import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { formatApiError, photoUrl, formatMartiniqueDateTime } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Trophy, AlertTriangle, Loader2, Star } from "lucide-react";
import { toast } from "sonner";
import { useCountdown } from "@/hooks/useCountdown";
import { RankingPicker } from "@/components/RankingPicker";

export default function StageDetail() {
  const { id } = useParams();
  const { refreshUser, user } = useAuth();
  const [stage, setStage] = useState(null);
  const [yoles, setYoles] = useState([]);
  const [rankings, setRankings] = useState([]);
  const [myRanking, setMyRanking] = useState(null);
  const [selected, setSelected] = useState([]);
  const [capsize, setCapsize] = useState([]);
  const [saving, setSaving] = useState(false);
  const [budget, setBudget] = useState(null); // {max, used, remaining, per_stage}

  const load = async () => {
    const [s, y, list, me] = await Promise.all([
      api.get(`/stages/${id}`),
      api.get(`/yoles`),
      api.get(`/rankings/stage/${id}`),
      api.get(`/rankings/stage/${id}/me`),
    ]);
    setStage(s.data);
    setYoles(y.data);
    setRankings(list.data);
    setMyRanking(me.data);
    if (me.data) {
      setSelected(me.data.ranking || []);
      setCapsize(me.data.capsize_predictions || []);
    }
    // Fetch season-wide capsize budget for this user (scoped to the stage's season)
    if (s.data?.season_id) {
      try {
        const b = await api.get(`/rankings/capsize-budget?season_id=${s.data.season_id}`);
        setBudget(b.data);
      } catch { /* non-blocking */ }
    }
  };

  useEffect(() => { load(); }, [id]);

  const cd = useCountdown(stage?.date);

  if (!stage) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-[color:var(--coral)]" />
      </div>
    );
  }

  const yoleById = Object.fromEntries(yoles.map((y) => [y.id, y]));
  const canSubmit = stage.status === "upcoming" && !cd.closed && (user?.league_id);

  const submit = async () => {
    if (selected.length < 5) {
      toast.error("Classe au moins les 5 premières yoles");
      return;
    }
    setSaving(true);
    try {
      await api.post("/rankings", {
        stage_id: id,
        ranking: selected,
        capsize_predictions: capsize,
      });
      toast.success(myRanking ? "Pronostic mis à jour" : "Pronostic enregistré !");
      await Promise.all([load(), refreshUser()]);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const winner = stage.winner_yole_id ? yoleById[stage.winner_yole_id] : null;
  const capsized = (stage.capsized_yole_ids || []).map((yid) => yoleById[yid]).filter(Boolean);
  const finalRanking = (stage.final_ranking || []).map((yid) => yoleById[yid]).filter(Boolean);

  return (
    <div className="space-y-6">
      <Link to="/etapes" data-testid="back-to-stages" className="inline-flex items-center gap-1.5 text-sm font-bold text-slate-500 hover:text-[color:var(--coral)]">
        <ArrowLeft className="h-4 w-4" /> Retour
      </Link>

      <div className="relative overflow-hidden rounded-3xl bg-[color:var(--deep)] text-white p-6 md:p-8">
        <img
          src="https://images.unsplash.com/photo-1501604914713-3decf20bf804?crop=entropy&cs=srgb&fm=jpg&q=85"
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-25"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[color:var(--deep)]/85 to-transparent" />
        <div className="relative">
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-[color:var(--sunshine)]">
            Étape {stage.stage_number}
            {stage.status === "settled" && <Badge className="ml-2 bg-slate-200 text-slate-700 border-0">Réglée</Badge>}
            {stage.status === "closed" && <Badge className="ml-2 bg-amber-200 text-amber-900 border-0">Paris fermés</Badge>}
          </div>
          <h1 className="mt-2 font-display text-3xl sm:text-4xl font-extrabold leading-tight">
            {stage.name}
          </h1>
          <div className="mt-2 text-sm opacity-80">
            {stage.start_village} → {stage.end_village} · {formatMartiniqueDateTime(stage.date, { year: "numeric" })}
          </div>
          <div className="mt-1 text-xs opacity-60">
            Départ &amp; clôture des paris — heure locale Martinique
          </div>
          {stage.status === "upcoming" && (
            <div className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur border border-white/20" data-testid="stage-countdown">
              <span className={`w-2 h-2 rounded-full ${cd.closed ? "bg-slate-400" : cd.urgent ? "bg-[color:var(--coral)] animate-pulse" : "bg-[color:var(--sunshine)]"}`} />
              <span className="text-xs font-bold uppercase tracking-wider">
                {cd.closed ? "Paris fermés" : `Fermeture des paris dans ${cd.label}`}
              </span>
            </div>
          )}
        </div>
      </div>

      {stage.status === "settled" && (
        <div className="bg-white border-2 border-[color:var(--sunshine)] rounded-2xl p-5" data-testid="stage-results">
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-amber-700 mb-3">
            <Trophy className="h-4 w-4" /> Classement final
          </div>
          {finalRanking.length === 0 ? (
            winner && (
              <div className="flex items-center gap-3">
                {winner.photo_url ? (
                  <img src={photoUrl(winner.photo_url)} alt={winner.name} className="w-14 h-14 rounded-xl object-cover flex-shrink-0" style={{ backgroundColor: winner.color }} />
                ) : (
                  <div className="w-14 h-14 rounded-xl flex-shrink-0 flex items-center justify-center" style={{ backgroundColor: winner.color }}>
                    <Trophy className="h-6 w-6 text-white/90" />
                  </div>
                )}
                <div>
                  <div className="font-display font-extrabold text-xl text-[color:var(--deep)]">{winner.name}</div>
                  <div className="text-sm text-slate-500">{winner.skipper}</div>
                </div>
              </div>
            )
          ) : (
            <div className="space-y-1.5">
              {finalRanking.map((y, i) => (
                <div key={y.id} className="flex items-center gap-3 py-1.5 border-b border-slate-100 last:border-0">
                  <div className={`w-7 h-7 rounded-full font-mono font-black text-xs flex items-center justify-center flex-shrink-0 ${
                    i === 0 ? "bg-[color:var(--sunshine)] text-[color:var(--deep)]" :
                    i === 1 ? "bg-slate-200 text-slate-700" :
                    i === 2 ? "bg-orange-200 text-orange-800" :
                    "bg-slate-100 text-slate-500"
                  }`}>
                    {i + 1}
                  </div>
                  {y.photo_url ? (
                    <img src={photoUrl(y.photo_url)} alt={y.name} className="w-8 h-8 rounded-lg object-cover" style={{ backgroundColor: y.color }} />
                  ) : (
                    <div className="w-8 h-8 rounded-lg" style={{ backgroundColor: y.color }} />
                  )}
                  <div className="font-bold text-sm">{y.name}</div>
                </div>
              ))}
            </div>
          )}
          {capsized.length > 0 && (
            <div className="mt-4 pt-4 border-t border-slate-100">
              <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-[color:var(--coral)] mb-2">
                <AlertTriangle className="h-3.5 w-3.5" /> Dessalages
              </div>
              <div className="flex flex-wrap gap-2">
                {capsized.map((y) => (
                  <div key={y.id} className="flex items-center gap-2 px-2.5 py-1 rounded-full bg-red-50 border border-red-100">
                    <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: y.color }} />
                    <span className="text-xs font-bold">{y.name}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {!user?.league_id && stage.status !== "settled" && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 text-sm text-amber-900">
          Tu dois d'abord <Link to="/profil" className="font-bold underline">rejoindre une ligue</Link> avant de faire ton pronostic.
        </div>
      )}

      {canSubmit && (
        <section>
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-display text-xl font-extrabold text-[color:var(--deep)]">Ton pronostic</h2>
            {user?.favorite_yole_id && (
              <div className="text-[11px] font-bold text-[color:var(--sunshine)] inline-flex items-center gap-1">
                <Star className="h-3 w-3 fill-current" /> +5 si {user.favorite_yole_name} gagne
              </div>
            )}
          </div>
          <RankingPicker
            value={selected}
            onChange={setSelected}
            yoles={yoles}
            minSize={5}
            capsizePicks={capsize}
            onCapsizeChange={setCapsize}
            budget={budget}
            currentStageId={id}
          />
          <Button
            onClick={submit}
            disabled={saving || selected.length < 5}
            data-testid="submit-ranking"
            className="mt-4 w-full h-12 rounded-full bg-[color:var(--coral)] hover:bg-[color:var(--coral)]/90 text-white font-bold btn-glow"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : myRanking ? "Mettre à jour mon pronostic" : "Valider mon pronostic"}
          </Button>
        </section>
      )}

      {rankings.length > 0 && (
        <section>
          <h2 className="font-display text-xl font-extrabold text-[color:var(--deep)] mb-3">Pronostics de ta ligue</h2>
          <div className="bg-white border border-slate-200 rounded-2xl divide-y divide-slate-100">
            {rankings.map((r) => (
              <div key={r.id} className="p-4" data-testid={`ranking-row-${r.id}`}>
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-xs font-bold">
                    {r.user_name?.[0]?.toUpperCase() || "?"}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-sm">{r.user_id === user?.id ? "Toi" : r.user_name}</div>
                    <div className="text-[11px] text-slate-500">
                      {r.status === "settled"
                        ? `+${r.total_points} pts`
                        : r.hidden
                        ? "Pronostic verrouillé jusqu'au départ"
                        : `${r.ranking.length} yoles classées`}
                    </div>
                  </div>
                  {r.status === "settled" && (
                    <div className="text-right text-[11px] font-mono text-slate-500">
                      classement: +{r.points_ranking} · dessal.: +{r.points_capsize} · fav.: +{r.points_favorite}
                    </div>
                  )}
                  {r.hidden && (
                    <div
                      className="text-[10px] font-black uppercase tracking-widest bg-slate-100 text-slate-500 rounded-full px-2 py-1 inline-flex items-center gap-1"
                      data-testid={`ranking-hidden-${r.id}`}
                      title="Visible après le départ de l'étape"
                    >
                      🔒 Secret
                    </div>
                  )}
                </div>
                {!r.hidden && (
                  <div className="flex flex-wrap gap-1">
                    {r.ranking.slice(0, 8).map((yid, i) => {
                      const y = yoleById[yid];
                      if (!y) return null;
                      return (
                        <div key={yid} className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-50 text-[10px] font-bold">
                          <span className="font-mono text-slate-500">{i + 1}.</span>
                          <div className="w-2 h-2 rounded-full" style={{ backgroundColor: y.color }} />
                          {y.name}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
